import { AnimatePresence, motion } from 'framer-motion';
import { rgba } from 'polished';
import Quill from 'quill';
import QuillDelta from 'quill-delta';
import { PropsWithChildren, RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useUploadAsset } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

import {
	ImageAspectRatio,
	ImageShape,
} from '../../../../../../../src/packages/kakapo-ui-kit/types/Image';
import ImageUploadModalContainer from '../../../../../../../src/settings/theme/ImageUploadModalContainer';
import { LanguageSelect } from '../../../common/LanguageSelect';
import { Popover } from '../../../common/Popover';
import { IconButton } from '../../../common/button/IconButton';
import {
	BoldIcon,
	ItalicIcon,
	LinkIcon,
	ListIcon,
	UnderlineIcon,
	UploadIcon,
} from '../../../icons';
import { tokens } from '../../../theme/tokens';
import QuillLinkProperties from './QuillLinkProperties';

const Container = styled(Flex)`
	background: ${tokens.surface.white};
	border-bottom: 1px solid ${tokens.border.subtle};
`;

const ToolbarButtons = styled(Flex)`
	border-radius: 4px;
	background: white;
	box-shadow: 0px 1px 2px ${rgba(tokens.text.secondary, 0.16)};
	overflow: hidden;
`;

const MediaToolbarButton = styled(IconButton)`
	padding: 8px 16px;
	border-radius: 0;

	&:hover {
		background: ${tokens.surface.primary};
	}

	svg {
		display: block;
		width: 16px;
		height: 16px;

		path {
			fill: ${(props) => props.theme.colors.black};
		}
	}
`;

const ToolbarButtonContainer = styled(Box)<{ active?: boolean }>`
	padding: 8px 16px;
	flex-grow: 0;
	flex-shrink: 0;
	cursor: pointer;

	svg {
		display: block;
		width: 16px;
		height: 16px;
	}

	&:not(:first-child) {
		border-left: 1px solid
			${(props) => (props.active ? props.theme.colors.primary : tokens.border.subtle)};
	}

	&:hover {
		background: ${(props) => (props.active ? props.theme.colors.primary : tokens.surface.primary)};
	}

	${(props) =>
		props.active &&
		`background: ${props.theme.colors.primary};
		color: ${tokens.text.inverse};
		margin-right: -1px;
		padding-right: 17px;
		
		svg {
			path:not([fill='none']) {
				fill: currentColor;
			}
		}`}
`;

const ToolbarMessage = styled(Box)`
	margin-right: 4px;
	color: ${(props) => props.theme.colors.primary};
`;

const HeadingSelect = styled.select`
	appearance: none;
	border: none;
	background: transparent;
	font-size: 13px;
	font-weight: 500;
	padding: 8px 24px 8px 12px;
	cursor: pointer;
	color: ${(props) => props.theme.colors.black};
	background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
	background-repeat: no-repeat;
	background-position: right 8px center;

	&:focus {
		outline: none;
	}
`;

function useRerender() {
	const [, setValue] = useState(false);

	return () => setValue((value) => !value);
}

function ToolbarButton<T>(
	props: PropsWithChildren<{
		innerRef: RefObject<Quill | undefined>;
		name: string;
		value?: (initialValue?: T | unknown) => Promise<T | undefined>;
	}>,
) {
	const rerender = useRerender();
	const initialValue = props.innerRef.current?.getSelection()
		? props.innerRef.current?.getFormat()[props.name]
		: undefined;

	const quill = props.innerRef.current;

	function onSelectionChange() {
		rerender();
	}

	useEffect(() => {
		quill?.on('selection-change', onSelectionChange);

		return () => {
			quill?.off('selection-change', onSelectionChange);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [quill]);

	return (
		<ToolbarButtonContainer
			active={!!initialValue}
			onMouseDown={(event) => event.preventDefault()}
			onClick={async () => {
				const value = props.value ? await props.value?.(initialValue) : !initialValue;
				props.innerRef.current?.focus();
				props.innerRef.current?.format(
					props.name,
					props.name === 'list' ? 'bullet' : value,
					'user',
				);
				rerender();
			}}
		>
			{props.children}
		</ToolbarButtonContainer>
	);
}

export function QuillToolbar(props: {
	hasMediaUpload?: boolean;
	hasHeading?: boolean;
	hasRightAlignment?: boolean;
	hideLink?: boolean;
	innerRef: RefObject<Quill | undefined>;
	localizable?: boolean;
	message?: string;
	onChange?: (value: QuillDelta) => void;
}) {
	const { t } = useTranslation();
	const [uploadBackground] = useUploadAsset();

	const [link, setLink] = useState<
		{ initialValue?: string; resolve: (value?: string) => void } | undefined
	>();
	const [showImageUpload, setShowImageUpload] = useState(false);
	const [headerLevel, setHeaderLevel] = useState<string>('');

	const quill = props.innerRef.current;

	useEffect(() => {
		if (!props.hasHeading || !quill) return;

		const onSelectionChange = () => {
			const format = quill.getSelection() ? quill.getFormat() : {};
			setHeaderLevel(format.header ? String(format.header) : '');
		};

		quill.on('selection-change', onSelectionChange);
		return () => {
			quill.off('selection-change', onSelectionChange);
		};
	}, [props.hasHeading, quill]);

	const hasQuillPhotoVideoFeature = useFeature(graphql.FeatureName.QuillPhotoVideo);

	const upload = async (blob: Blob) => {
		const image = await uploadBackground(blob);
		const type = blob.type.split('/')[0];
		props.innerRef.current?.insertEmbed(0, type, image.source.url);
		props.onChange?.(props.innerRef.current?.getContents() as QuillDelta);
		setShowImageUpload(false);
	};

	const mediaModalOrigin = props.hasRightAlignment
		? { top: 0, left: 0, width: 0, height: 0, right: 0 }
		: { top: 0, left: 0, width: 0, height: 0 };

	return (
		<Container p={1} alignItems="center">
			{props.hasHeading && (
				<ToolbarButtons mr={2}>
					<HeadingSelect
						value={headerLevel}
						onMouseDown={(e) => e.stopPropagation()}
						onChange={(e) => {
							const value = e.target.value;
							setHeaderLevel(value);
							props.innerRef.current?.focus();
							props.innerRef.current?.format('header', value ? Number(value) : false, 'user');
						}}
					>
						<option value="">Normal</option>
						<option value="1">Heading 1</option>
						<option value="2">Heading 2</option>
						<option value="3">Heading 3</option>
					</HeadingSelect>
				</ToolbarButtons>
			)}

			<ToolbarButtons>
				<ToolbarButton innerRef={props.innerRef} name="bold">
					<BoldIcon />
				</ToolbarButton>

				<ToolbarButton innerRef={props.innerRef} name="italic">
					<ItalicIcon />
				</ToolbarButton>

				<ToolbarButton innerRef={props.innerRef} name="underline">
					<UnderlineIcon />
				</ToolbarButton>
			</ToolbarButtons>

			<ToolbarButtons ml={2}>
				<ToolbarButton innerRef={props.innerRef} name="list">
					<ListIcon />
				</ToolbarButton>
			</ToolbarButtons>

			{!props.hideLink && (
				<ToolbarButtons ml={2}>
					<Popover.Target
						contents={
							<QuillLinkProperties
								initialValue={link?.initialValue}
								onDismiss={(value) => {
									props.innerRef.current?.focus();

									if (value === '') {
										link?.resolve(undefined);
									} else if (value) {
										link?.resolve(value);
									}

									setLink(undefined);
								}}
							/>
						}
						visible={!!link}
						onDismiss={() => {
							setLink(undefined);
							props.innerRef.current?.focus();
						}}
					>
						<ToolbarButton
							innerRef={props.innerRef}
							name="link"
							value={(initialValue?: string) =>
								new Promise<string | undefined>((resolve) => {
									setLink({ initialValue, resolve });
								})
							}
						>
							<LinkIcon />
						</ToolbarButton>
					</Popover.Target>
				</ToolbarButtons>
			)}

			{hasQuillPhotoVideoFeature && props.hasMediaUpload && (
				<ToolbarButtons ml={2}>
					<MediaToolbarButton
						icon={<UploadIcon />}
						type="plain"
						onClick={() => {
							localStorage.setItem('selectingMedia', 'true');
							setShowImageUpload(true);
						}}
					/>
				</ToolbarButtons>
			)}

			{props.localizable && (
				<>
					<Box flexGrow={1} />

					<ToolbarButtons ml={2}>
						<LanguageSelect.Scope />
					</ToolbarButtons>
				</>
			)}

			{showImageUpload && (
				<ImageUploadModalContainer
					onSubmit={(blob) => {
						localStorage.removeItem('selectingMedia');
						upload(blob!);
					}}
					imageSpecification={{
						title: t('common.mediaUpload'),
						key: 'media',
						aspectRatio: ImageAspectRatio.Header,
						shape: ImageShape.Rectangular,
						allowedExtensions: [
							'image/png',
							'image/jpeg',
							'image/svg+xml',
							'application/xml',
							'video/avi',
							'video/webm',
							'video/mp4',
						],
					}}
					onCancel={() => {
						localStorage.removeItem('selectingMedia');
						setShowImageUpload(false);
					}}
					origin={mediaModalOrigin}
					hasVideo
				/>
			)}

			<AnimatePresence>
				{props.message && (
					<motion.div
						style={{ marginLeft: 'auto' }}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2, ease: 'easeInOut' }}
					>
						<ToolbarMessage>{props.message}</ToolbarMessage>
					</motion.div>
				)}
			</AnimatePresence>
		</Container>
	);
}
